// RimoCompta's three hook-ups for the suite app switcher (RimoCore/shared/suite, user request 19.10.2026): the list from the
// BFF (`core/suite/apps` → RimoID), the logo (marked `data-suite-logo` by the design compiler) and how a program opens.
import { invoke } from '@tauri-apps/api/core';
import { api, tokens } from './api/client';
import { useEffect } from 'react';
import { runsOn, SuiteSelect, type SuiteApp } from '@rimocore/shared/suite/SuiteSelect';
import { deviceId, focusApp, startPresence } from './desktop/presence';
import { tr2 } from './i18n/lang';
import { isDesktop } from './platform';

export type OpenTarget = { kind: 'desktop'; key: string; scheme: string | null; url: string } | { kind: 'tab'; url: string };
/** Browser: the program's address in this tab. Desktop app: its desktop app when installed, else the system browser (Rust). */
export const openTarget = (app: SuiteApp, desktop: boolean): OpenTarget =>
  desktop ? { kind: 'desktop', key: app.key, scheme: app.desktopScheme ?? null, url: app.url } : { kind: 'tab', url: app.url };

export async function openSuiteApp(app: SuiteApp): Promise<void> {
  // AE: its desktop app already runs on this computer → RimoID brings it to the front (else the scheme as before).
  if (isDesktop() && runsOn(app, deviceId()) && await focusApp(app.key)) return;
  const t = openTarget(app, isDesktop());
  if (t.kind === 'tab') { location.assign(t.url); return; }
  await invoke('open_suite_app', { key: t.key, scheme: t.scheme, url: t.url });
}

export const loadApps = () => api<SuiteApp[]>('/core/suite/apps');

/** P1 „Über RimoPrint drucken“: RimoPrint's print page with one RimoCompta document (its own app page, never the file). */
export const rimoPrintLink = (app: SuiteApp, documentId: string) => {
  const link = 'print?source=rimocompta&id=' + encodeURIComponent(documentId);
  return { link, url: new URL('/app/' + link, app.url).toString() };
};
/** Vertrag G „Ziel Personnel“: RimoPrint's scan page aimed at a person's file (no name in the link; kind = a suggestion). */
export const rimoPrintScanLink = (app: SuiteApp, personId: string, kind: string) => {
  const link = 'scan?target=personnel&personId=' + encodeURIComponent(personId) + '&kind=' + encodeURIComponent(kind);
  return { link, url: new URL('/app/' + link, app.url).toString() };
};
export async function openInRimoPrint(app: SuiteApp, documentId: string): Promise<void> { return openRimoPrint(app, rimoPrintLink(app, documentId)); }
export async function openRimoPrint(app: SuiteApp, { link, url }: { link: string; url: string }): Promise<void> {
  // Browser: a new tab, so the import stays open here. Desktop: RimoPrint's desktop app (rimoprint://print?…), else the browser.
  if (!isDesktop()) { window.open(url, '_blank', 'noopener'); return; }
  await invoke('open_suite_app', { key: app.key, scheme: app.desktopScheme ?? null, url, link });
}
// A refreshed token can carry changed rights (RimoID events end in a refresh): then the list is fetched again.
// AE: in the desktop app also every minute – which programs run on this computer changes (RimoID's `devices`).
const subscribe = (reload: () => void) => {
  let last = tokens.access;
  const off = tokens.onChange((t) => { if (t && t !== last) { last = t; reload(); } });
  const every = isDesktop() ? setInterval(reload, 60_000) : undefined;
  return () => { off(); if (every) clearInterval(every); };
};

export function SuiteSwitcher() {
  // AE: heartbeat and RimoID's event stream while signed in (desktop app only).
  useEffect(() => startPresence(), []);
  return <SuiteSelect current="rimocompta" anchor="[data-suite-logo]" loadApps={loadApps} openApp={openSuiteApp} subscribe={subscribe}
    running={(a) => isDesktop() && runsOn(a, deviceId())} runningLabel={tr2('In der App öffnen', 'Ouvrir dans l’app')} />;
}
